import { useViewjobOneGraphModel } from "../context/ViewJobDataContext";

export enum LocationDisplayType {
  LOCATION_WITH_REMOTE = "locationWithRemote",
  ADDRESS_WITH_REMOTE = "addressWithRemote",
  STREET_ADDRESS = "streetAddress",
  LOCATION_ONLY = "locationOnly",
  REMOTE_ONLY = "remoteOnly",
  NONE = "none",
}

export interface LocationDisplay {
  type: LocationDisplayType;
  resolvedLocation?: string;
}

export function getResolvedLocation(
  trueJobLocation?: string,
  formattedLocation?: string,
): string | undefined {
  return trueJobLocation || formattedLocation || undefined;
}

export function shouldShowStreetAddress(trueJobLocation?: string, streetAddress?: string): boolean {
  return !trueJobLocation && !!streetAddress;
}

export function shouldShowLocationPrefix(
  trueJobLocation?: string,
  remoteLocation?: boolean,
): boolean {
  return !!trueJobLocation || !remoteLocation;
}

export interface UseLocationDisplayParams {
  formattedLocation?: string;
  remoteLocation?: boolean;
  remoteWorkModel?: {
    text?: string;
    inlineText?: boolean;
  };
  streetAddress?: string;
  shouldInlineRemoteText?: boolean;
  preferAddressWithRemote?: boolean;
  allowRemoteWithoutInline?: boolean;
}

/**
 * Returns the active trueJobLocation if the proctor is enabled and the
 * server provided a value, otherwise undefined.
 */
export function useTrueJobLocation(): string | undefined {
  const viewjobOneGraphModel = useViewjobOneGraphModel();
  const isTrueJobLocationActive = viewjobOneGraphModel?.features?.isTrueJobLocationActive;
  const rawTrueJobLocation = viewjobOneGraphModel?.job?.trueJobLocation;
  return isTrueJobLocationActive && rawTrueJobLocation ? rawTrueJobLocation : undefined;
}

/**
 * Resolves the active trueJobLocation from the OneGraph model and returns
 * a discriminated union describing which location content to render.
 */
export function useLocationDisplay(params: UseLocationDisplayParams): LocationDisplay {
  const trueJobLocation = useTrueJobLocation();
  return resolveLocationDisplay({ ...params, trueJobLocation });
}

export interface UseLocationDisplayFromContextOptions {
  shouldInlineRemoteText?: boolean;
  preferAddressWithRemote?: boolean;
  allowRemoteWithoutInline?: boolean;
}

/**
 * Reads job location data from the ViewjobOneGraphModel context and resolves
 * the LocationDisplay. Accepts optional overrides for display behavior;
 * defaults `shouldInlineRemoteText` to the model's `remoteWorkModel.inlineText`.
 */
export function useLocationDisplayFromContext(
  options?: UseLocationDisplayFromContextOptions,
): LocationDisplay {
  const viewjobOneGraphModel = useViewjobOneGraphModel();
  const trueJobLocation = useTrueJobLocation();
  const job = viewjobOneGraphModel?.job;

  if (!job) {
    return { type: LocationDisplayType.NONE };
  }

  return resolveLocationDisplay({
    formattedLocation: job.formattedLocation,
    remoteLocation: job.remoteLocation,
    remoteWorkModel: job.remoteWorkModel,
    streetAddress: job.location?.formattedStreetAddress,
    shouldInlineRemoteText: options?.shouldInlineRemoteText ?? !!job.remoteWorkModel?.inlineText,
    preferAddressWithRemote: options?.preferAddressWithRemote,
    allowRemoteWithoutInline: options?.allowRemoteWithoutInline,
    trueJobLocation,
  });
}

interface ResolveLocationDisplayParams extends UseLocationDisplayParams {
  trueJobLocation?: string;
}

/**
 * Pure function that computes the LocationDisplay from all inputs.
 * Separated from the hook for testability.
 */
export function resolveLocationDisplay({
  trueJobLocation,
  formattedLocation,
  remoteLocation,
  remoteWorkModel,
  streetAddress,
  shouldInlineRemoteText,
  preferAddressWithRemote,
  allowRemoteWithoutInline,
}: ResolveLocationDisplayParams): LocationDisplay {
  const resolvedLocation = getResolvedLocation(trueJobLocation, formattedLocation);
  const showStreetAddress = shouldShowStreetAddress(trueJobLocation, streetAddress);
  const remoteWorkText = remoteWorkModel?.text;
  const showInlineRemoteText = !!(shouldInlineRemoteText && remoteWorkText);

  if (preferAddressWithRemote && !remoteLocation && showStreetAddress && showInlineRemoteText) {
    return {
      type: LocationDisplayType.ADDRESS_WITH_REMOTE,
      resolvedLocation: streetAddress,
    };
  }
  if (resolvedLocation && showInlineRemoteText) {
    const showPrefix = shouldShowLocationPrefix(trueJobLocation, !!remoteLocation);
    if (showPrefix) {
      return {
        type: LocationDisplayType.LOCATION_WITH_REMOTE,
        resolvedLocation,
      };
    }
    return { type: LocationDisplayType.REMOTE_ONLY };
  }
  if (showStreetAddress) {
    return { type: LocationDisplayType.STREET_ADDRESS };
  }
  if (resolvedLocation) {
    return { type: LocationDisplayType.LOCATION_ONLY, resolvedLocation };
  }
  if (allowRemoteWithoutInline ? remoteWorkText : showInlineRemoteText) {
    return { type: LocationDisplayType.REMOTE_ONLY };
  }
  return { type: LocationDisplayType.NONE };
}
